import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Switch,
  FormHelperText,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Translate as TranslateIcon,
  Code as CodeIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  UploadFile as UploadFileIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import {
  extractTranslationObject,
  normalizeTranslationContent,
  stringifyTranslation,
} from "../utils/translationParser";
import TranslationService, {
  ValidationError,
  TranslationChunk,
  TranslationJob as TranslationJobType,
} from "../services/TranslationService";
import {
  filterStoreLocalesBySupport,
  STORE_LOCALE_SELECTION_OPTIONS,
  type StoreLocaleSupportFilter,
} from "../utils/storeLocales";

const AVAILABLE_LOCALES = STORE_LOCALE_SELECTION_OPTIONS;

// Sample of the translation file structure (for validation)
const SAMPLE_TRANSLATION = {
  navigation: {
    home: "Home",
    settings: "Settings",
  },
  buttons: {
    save: "Save",
  },
};

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).format(date);

const getChunkStatusColor = (
  status: TranslationChunk["status"],
): "success" | "warning" | "error" | "info" => {
  switch (status) {
    case "completed":
      return "success";
    case "processing":
      return "info";
    case "failed":
      return "error";
    case "pending":
    default:
      return "warning";
  }
};

const TranslationJob: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const isCreateMode = !jobId || jobId === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const { user, githubConnected } = useAuth();

  // Get repo and owner from URL query params
  const queryParams = new URLSearchParams(location.search);
  const ownerFromQuery = queryParams.get("owner");
  const repoFromQuery = queryParams.get("repo");

  // State for form inputs
  const [fileContent, setFileContent] = useState<string>("");
  const [sourceFileName, setSourceFileName] =
    useState<string>("source_file.txt");
  const [selectedLocales, setSelectedLocales] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerName] = useState<string>(ownerFromQuery || "wejeel");
  const [repoName] = useState<string>(repoFromQuery || "Translations");
  const [createPR, setCreatePR] = useState<boolean>(true);

  // Validation states
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValidContent, setIsValidContent] = useState<boolean | null>(null);
  const [showValidationDialog, setShowValidationDialog] =
    useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [jobDetails, setJobDetails] = useState<TranslationJobType | null>(null);
  const [jobChunks, setJobChunks] = useState<TranslationChunk[]>([]);
  const [translatedObject, setTranslatedObject] = useState<any | null>(null);
  const [loadingJobDetails, setLoadingJobDetails] = useState<boolean>(false);
  const [submittedJobIds, setSubmittedJobIds] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState({
    setup: true,
    content: true,
    languages: true,
    review: true,
  });
  const [localeSupportFilter, setLocaleSupportFilter] =
    useState<StoreLocaleSupportFilter>("boiler_plate");
  const [localeSearchQuery, setLocaleSearchQuery] = useState("");

  const filteredLocales = useMemo(() => {
    const normalizedQuery = localeSearchQuery.trim().toLowerCase();

    return filterStoreLocalesBySupport(AVAILABLE_LOCALES, localeSupportFilter)
      .filter((locale) => {
        if (!normalizedQuery) {
          return true;
        }

        const searchableText = `${locale.code} ${locale.name}`.toLowerCase();
        return searchableText.includes(normalizedQuery);
      })
      .sort((left, right) =>
        left.name.localeCompare(right.name, undefined, {
          sensitivity: "base",
        }),
      );
  }, [localeSearchQuery, localeSupportFilter]);

  const allLocalesSelected =
    filteredLocales.length > 0 &&
    filteredLocales.every((locale) => selectedLocales.includes(locale.code));
  const partiallySelectedLocales =
    filteredLocales.some((locale) => selectedLocales.includes(locale.code)) &&
    !allLocalesSelected;

  const handleSectionToggle =
    (section: keyof typeof expandedSections) =>
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedSections((prev) => ({ ...prev, [section]: expanded }));
    };

  // Handle locale selection change
  const handleLocaleChange =
    (locale: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedLocales((prev) =>
          prev.includes(locale) ? prev : [...prev, locale],
        );
      } else {
        setSelectedLocales((prev) => prev.filter((l) => l !== locale));
      }
    };

  // Show snackbar message
  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const loadJobDetails = async () => {
      if (isCreateMode || !user?.uid || !jobId) {
        return;
      }

      setLoadingJobDetails(true);
      setError(null);

      try {
        const details = await TranslationService.getJobDetails(user.uid, jobId);
        setJobDetails(details.job);
        setJobChunks(details.chunks);
        setTranslatedObject(details.translatedContent);
      } catch (err: any) {
        console.error("Error loading translation job details:", err);
        setError(`Failed to load job details: ${err.message}`);
      } finally {
        setLoadingJobDetails(false);
      }
    };

    loadJobDetails();
  }, [isCreateMode, jobId, user]);

  const handleCopyTranslatedObject = async () => {
    if (!translatedObject) {
      showSnackbar("No translated object available yet.");
      return;
    }

    try {
      await navigator.clipboard.writeText(
        JSON.stringify(translatedObject, null, 2),
      );
      showSnackbar("Translated object copied to clipboard.");
    } catch (err: any) {
      showSnackbar(`Failed to copy translation object: ${err.message}`);
    }
  };

  // Validate the translation content
  const validateContent = () => {
    setIsValidating(true);
    setValidationErrors([]);
    setError(null);

    try {
      // Normalize content from string to object (handles JSON, TS, JS formats)
      const contentObj = normalizeTranslationContent(fileContent);

      if (!contentObj) {
        setIsValidContent(false);
        setValidationErrors([
          {
            path: "",
            message:
              "Unable to parse content. Please provide valid JSON or TypeScript/JavaScript export format.",
          },
        ]);
        showSnackbar("Invalid format");
        setIsValidating(false);
        return;
      }

      // Update the file content to show the normalized JSON format
      if (typeof fileContent === "string") {
        const normalizedStr = stringifyTranslation(contentObj);
        if (normalizedStr !== fileContent) {
          setFileContent(normalizedStr);
          showSnackbar("Content normalized to JSON format");
        }
      }

      // Basic validation
      const basicValid =
        TranslationService.validateTranslationContent(contentObj);
      if (!basicValid) {
        setIsValidContent(false);
        setValidationErrors([
          {
            path: "",
            message: "Translation content must be a valid JSON object.",
          },
        ]);
        showSnackbar("Invalid translation content");
        setIsValidating(false);
        return;
      }

      // Deep validation
      const errors = TranslationService.deepValidateTranslation(contentObj);
      setValidationErrors(errors);

      if (errors.length > 0) {
        setIsValidContent(false);
        showSnackbar(`Validation failed with ${errors.length} errors`);
        setShowValidationDialog(true);
      } else {
        setIsValidContent(true);
        showSnackbar("Translation content is valid");
      }
    } catch (error: any) {
      setIsValidContent(false);
      setError(error.message);
      showSnackbar(`Validation error: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  // Load sample translation
  const loadSampleTranslation = () => {
    setFileContent(JSON.stringify(SAMPLE_TRANSLATION, null, 2));
    showSnackbar("Sample translation loaded");
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSourceFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      // For TS/JS files, try to extract the translation object
      if (file.name.endsWith(".ts") || file.name.endsWith(".js")) {
        const extractedObject = extractTranslationObject(content);

        if (extractedObject) {
          // Display the extracted object in the editor as formatted JSON
          setFileContent(JSON.stringify(extractedObject, null, 2));
          showSnackbar(`Successfully extracted translations from ${file.name}`);
        } else {
          // If extraction failed, show the raw content
          setFileContent(content);
          showSnackbar(
            `Could not extract translation object from ${file.name}. Check format.`,
          );
        }
      } else {
        // For other file types, show content as-is
        setFileContent(content);
      }

      // Reset validation state
      setIsValidContent(null);
    };

    reader.readAsText(file);
  };

  // Submit the translation job
  const handleSubmit = async () => {
    if (fileContent.trim() === "") {
      setError("Please provide file content to translate.");
      return;
    }

    if (selectedLocales.length === 0) {
      setError("Please select at least one locale for translation.");
      return;
    }

    const currentUser = user;
    if (!currentUser) {
      setError("You must be logged in to submit translation jobs.");
      return;
    }

    if (!currentUser.uid) {
      setError("Missing Firebase user context. Please sign in again.");
      return;
    }

    if (!githubConnected || !currentUser.token) {
      setError(
        "Please connect your GitHub token before starting a translation job.",
      );
      return;
    }

    // Final validation before submitting
    try {
      // Normalize content from string to object (handles JSON, TS, JS formats)
      const contentToTranslate = normalizeTranslationContent(fileContent);

      if (!contentToTranslate) {
        setError(
          "Unable to parse content. Please provide valid JSON or TypeScript/JavaScript export format.",
        );
        return;
      }

      // Validate content
      if (!TranslationService.validateTranslationContent(contentToTranslate)) {
        setError(
          "Invalid translation content format. Please check your input.",
        );
        return;
      }

      const errors =
        TranslationService.deepValidateTranslation(contentToTranslate);
      if (errors.length > 0) {
        setError(
          `Validation failed with ${errors.length} errors. Please fix them before submitting.`,
        );
        setValidationErrors(errors);
        setShowValidationDialog(true);
        return;
      }

      setIsSubmitting(true);
      setError(null);
      const createdJobIds: string[] = [];
      const requestId = `tx-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      for (const locale of selectedLocales) {
        // Create a translation job for each selected locale
        const createdJobId = await TranslationService.createTranslationJob(
          ownerName,
          repoName,
          sourceFileName,
          locale,
          contentToTranslate,
          currentUser.uid,
          createPR,
          requestId,
        );
        createdJobIds.push(createdJobId);

        console.log(
          `Translation job created for ${locale} with ID: ${createdJobId}`,
        );
      }

      setSubmittedJobIds(createdJobIds);
      setIsSubmitting(false);

      // Show success message and keep user on success screen
      showSnackbar(
        `Successfully created ${selectedLocales.length} translation job(s)!`,
      );
    } catch (err: any) {
      console.error("Error submitting translation job:", err);
      setError(`Failed to submit translation job: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  if (!isCreateMode) {
    return (
      <Box sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ display: "flex", alignItems: "center", mb: 1 }}
            >
              <TranslateIcon sx={{ mr: 1 }} />
              Translation Job Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Job ID: {jobId}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/translation")}
            >
              Back to Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {loadingJobDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : jobDetails ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Locale
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {jobDetails.locale.toUpperCase()}
                    </Typography>
                    <Chip
                      size="small"
                      sx={{ mt: 1.5 }}
                      label={jobDetails.status}
                      color={
                        jobDetails.status === "completed"
                          ? "success"
                          : jobDetails.status === "error"
                            ? "error"
                            : jobDetails.status === "in-progress"
                              ? "info"
                              : "warning"
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Chunks
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {jobDetails.completedChunks} / {jobDetails.totalChunks}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {jobChunks.length} chunk record
                      {jobChunks.length === 1 ? "" : "s"} loaded
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Repository
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {jobDetails.owner}/{jobDetails.repo}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {jobDetails.sourcePath}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", md: "center" },
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6">Job Progress</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created {formatDate(jobDetails.createdAt)} • Updated{" "}
                    {formatDate(jobDetails.updatedAt)}
                  </Typography>
                </Box>
                <Typography variant="h6">
                  {Math.round(jobDetails.progress || 0)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={jobDetails.progress || 0}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Paper>

            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                      flexDirection: { xs: "column", md: "row" },
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6">Translated Object</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Output for this locale-specific translation job
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyTranslatedObject}
                      disabled={!translatedObject}
                    >
                      Copy Object
                    </Button>
                  </Box>

                  {translatedObject ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={18}
                      value={JSON.stringify(translatedObject, null, 2)}
                      InputProps={{ readOnly: true }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                  ) : (
                    <Alert severity="info">
                      No translated object is available yet for this job. It may
                      still be queued or processing.
                    </Alert>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Chunk Breakdown
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {jobChunks.map((chunk, index) => (
                      <ListItem
                        key={chunk.id}
                        divider={index < jobChunks.length - 1}
                        sx={{ px: 0, py: 1.5, display: "block" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                          }}
                        >
                          <ListItemText
                            sx={{ m: 0, pr: { xs: 0, sm: 2 } }}
                            primary={chunk.keyPath}
                            secondary={`Updated ${formatDate(chunk.updatedAt)}`}
                            primaryTypographyProps={{
                              sx: { wordBreak: "break-word" },
                            }}
                            secondaryTypographyProps={{
                              sx: { wordBreak: "break-word" },
                            }}
                          />
                          <Chip
                            size="small"
                            label={chunk.status}
                            color={getChunkStatusColor(chunk.status)}
                            sx={{
                              alignSelf: { xs: "flex-start", sm: "center" },
                            }}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>

                  {jobChunks.length === 0 && (
                    <Alert severity="info">
                      No chunk records found for this job.
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : (
          <Alert severity="info">No job details found.</Alert>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {submittedJobIds.length > 0 ? (
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 4 },
            border: "1px solid",
            borderColor: "success.main",
            bgcolor: "success.50",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <CheckIcon color="success" />
            <Typography variant="h5" fontWeight={700}>
              Translation job submitted
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your translation request is now queued for processing.
          </Typography>
          <Alert severity="success" sx={{ mb: 2 }}>
            Created {submittedJobIds.length} job
            {submittedJobIds.length === 1 ? "" : "s"} for {ownerName}/{repoName}
            .
          </Alert>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/translation")}
            >
              Monitor Job on Translation Page
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSubmittedJobIds([]);
                setError(null);
                setSelectedLocales([]);
                setIsValidContent(null);
                setValidationErrors([]);
              }}
            >
              Start Another Job
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Typography
            variant="h4"
            component="h1"
            sx={{ mb: 2, display: "flex", alignItems: "center" }}
          >
            <TranslateIcon sx={{ mr: 1 }} />
            {isCreateMode
              ? "New Translation Job"
              : `Edit Translation Job: ${jobId}`}
          </Typography>

          <Alert severity="info" sx={{ mb: 4 }} icon={<InfoIcon />}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              🚀 AI-Powered Translation with Gemini 2.5-flash
            </Typography>
            <Typography variant="body2">
              Your content will be automatically translated using Google's
              advanced AI model with 99%+ accuracy. Translations are processed
              in batches and will be ready within minutes. A pull request will
              be created with all translations for your review.
            </Typography>
          </Alert>

          <Alert
            severity={githubConnected ? "success" : "warning"}
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => navigate("/settings")}
              >
                Settings
              </Button>
            }
          >
            {githubConnected
              ? "GitHub access is connected. Manage it from Settings."
              : "Connect GitHub access in Settings before starting a translation job."}
          </Alert>

          <Accordion
            expanded={expandedSections.setup}
            onChange={handleSectionToggle("setup")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>
                1. Job Setup
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    id="ownerName"
                    label="Repository Owner"
                    value={ownerName}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <PersonIcon color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={createPR}
                          onChange={(e) => setCreatePR(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Create Pull Request"
                      sx={{ mt: 1 }}
                    />
                    <FormHelperText>
                      Automatically create PR when translation is complete
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSections.content}
            onChange={handleSectionToggle("content")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>
                2. Translation Content
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Provide a valid JSON object or upload a translation file
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Load a sample translation structure">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadSampleTranslation}
                      startIcon={<HelpIcon />}
                    >
                      Sample
                    </Button>
                  </Tooltip>
                  <Tooltip title="Upload JSON, JS, or TS translation file">
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        onChange={handleFileUpload}
                        accept=".json,.js,.ts,.txt"
                      />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                fullWidth
                id="fileContent"
                multiline
                rows={12}
                value={fileContent}
                onChange={(e) => {
                  setFileContent(e.target.value);
                  setIsValidContent(null);
                }}
                InputProps={{
                  startAdornment: (
                    <CodeIcon
                      color="action"
                      sx={{ mr: 1, alignSelf: "start", mt: 1 }}
                    />
                  ),
                }}
                variant="outlined"
                placeholder="Paste your translation JSON here..."
                sx={{
                  "& .MuiInputBase-inputMultiline": {
                    fontFamily: "Consolas, 'Courier New', monospace",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderColor:
                      isValidContent === true
                        ? "success.main"
                        : isValidContent === false
                          ? "error.main"
                          : "inherit",
                  },
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={validateContent}
                  disabled={isValidating || !fileContent.trim()}
                  startIcon={
                    isValidating ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CheckIcon />
                    )
                  }
                >
                  {isValidating ? "Validating..." : "Validate Content"}
                </Button>
              </Box>

              {isValidContent === true && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Content validation passed!
                </Alert>
              )}

              {isValidContent === false && validationErrors.length > 0 && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setShowValidationDialog(true)}
                    >
                      View Details
                    </Button>
                  }
                >
                  {`${validationErrors.length} validation ${
                    validationErrors.length === 1 ? "error" : "errors"
                  } found.`}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSections.languages}
            onChange={handleSectionToggle("languages")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>
                3. Select Target Languages ({selectedLocales.length} selected)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Choose from the expanded locale set used across Google Play
                  and App Store listing workflows.
                </Typography>
                <ToggleButtonGroup
                  value={localeSupportFilter}
                  exclusive
                  size="small"
                  sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
                  onChange={(
                    _event,
                    value: StoreLocaleSupportFilter | null,
                  ) => {
                    if (value) {
                      setLocaleSupportFilter(value);
                    }
                  }}
                >
                  <ToggleButton value="boiler_plate">
                    Boiler plate locales
                  </ToggleButton>
                  <ToggleButton value="common">Common to both</ToggleButton>
                  <ToggleButton value="google_play">Google only</ToggleButton>
                  <ToggleButton value="app_store">Apple only</ToggleButton>
                  <ToggleButton value="all">All locales</ToggleButton>
                </ToggleButtonGroup>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={allLocalesSelected}
                      indeterminate={partiallySelectedLocales}
                      onChange={(event) =>
                        setSelectedLocales(
                          event.target.checked
                            ? Array.from(
                                new Set([
                                  ...selectedLocales,
                                  ...filteredLocales.map(
                                    (locale) => locale.code,
                                  ),
                                ]),
                              )
                            : selectedLocales.filter(
                                (code) =>
                                  !filteredLocales.some(
                                    (locale) => locale.code === code,
                                  ),
                              ),
                        )
                      }
                    />
                  }
                  label={`Select all visible locales (${filteredLocales.length})`}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Find locale"
                  placeholder="Type locale name or code"
                  value={localeSearchQuery}
                  onChange={(event) => setLocaleSearchQuery(event.target.value)}
                  sx={{ mt: 1.5 }}
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              {filteredLocales.length === 0 ? (
                <Alert severity="info">
                  No locales match the current filter.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {filteredLocales.map((locale) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={locale.code}>
                      <Card
                        variant="outlined"
                        sx={{
                          bgcolor: selectedLocales.includes(locale.code)
                            ? "primary.50"
                            : "background.paper",
                          border: selectedLocales.includes(locale.code)
                            ? "2px solid"
                            : "1px solid",
                          borderColor: selectedLocales.includes(locale.code)
                            ? "primary.main"
                            : "divider",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => {
                          const event = {
                            target: {
                              checked: !selectedLocales.includes(locale.code),
                            },
                          } as React.ChangeEvent<HTMLInputElement>;
                          handleLocaleChange(locale.code)(event);
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6" sx={{ mr: 1 }}>
                              {locale.flag}
                            </Typography>
                            <Checkbox
                              checked={selectedLocales.includes(locale.code)}
                              onChange={handleLocaleChange(locale.code)}
                              name={locale.code}
                              color="primary"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="medium">
                            {locale.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 1,
                            }}
                          >
                            <Chip
                              size="small"
                              label={locale.code.toUpperCase()}
                              sx={{ fontSize: "0.7rem" }}
                              color={
                                selectedLocales.includes(locale.code)
                                  ? "primary"
                                  : "default"
                              }
                              variant={
                                selectedLocales.includes(locale.code)
                                  ? "filled"
                                  : "outlined"
                              }
                            />
                            {locale.speakers ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.65rem" }}
                              >
                                {locale.speakers}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.65rem" }}
                              >
                                Store-supported locale
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSections.review}
            onChange={handleSectionToggle("review")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={700}>
                4. Review and Submit
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {selectedLocales.length > 0 && fileContent.trim() && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    bgcolor: "primary.50",
                    border: "1px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Translation Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Target Languages:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {selectedLocales.map((code) => {
                          const locale = AVAILABLE_LOCALES.find(
                            (item) => item.code === code,
                          );
                          return (
                            <Chip
                              key={code}
                              label={`${locale?.flag} ${locale?.name}`}
                              size="small"
                              color="primary"
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Estimated Time:
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 1 }}>
                        ~{Math.ceil(selectedLocales.length * 2)} minutes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Processing time may vary based on content size
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    !githubConnected ||
                    selectedLocales.length === 0 ||
                    !fileContent.trim() ||
                    isValidContent === false
                  }
                >
                  {isSubmitting ? "Submitting..." : "Start Translation Job"}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Validation errors dialog */}
          <Dialog
            open={showValidationDialog}
            onClose={() => setShowValidationDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              Validation Errors
            </DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" paragraph>
                  The following errors were found in your translation content:
                </Typography>
                <Box
                  sx={{
                    maxHeight: "300px",
                    overflow: "auto",
                    bgcolor: "grey.100",
                    p: 2,
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                  }}
                >
                  {validationErrors.map((err, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 1,
                        pb: 1,
                        borderBottom:
                          index < validationErrors.length - 1
                            ? "1px solid #ddd"
                            : "none",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="error.main"
                        fontWeight="bold"
                      >
                        Path: {err.path || "root"}
                      </Typography>
                      <Typography variant="body2">{err.message}</Typography>
                      {err.value !== undefined && (
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-all" }}
                        >
                          Value: {JSON.stringify(err.value)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowValidationDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar messages */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
          />
        </>
      )}
    </Box>
  );
};

export default TranslationJob;
